//#region src/scripts/extenders/header.js
/**
* Controls the sticky header and desktop navigation menus.
*/
var Header = class extends HTMLElement {
	/**
	* Creates the lifecycle state used by the header.
	*/
	constructor() {
		super();
		this.abortController = null;
		this.header = null;
		this.announcement = null;
		this.mobileNumber = null;
		this.activeMenu = null;
		this.hideTimeout = null;
		this.isTriggerHovered = false;
		this.isMenuHovered = false;
		this.handleScroll = this.handleScroll.bind(this);
	}
	/**
	* Adds event listeners whenever the element enters the document.
	*/
	connectedCallback() {
		if (this.abortController) return;
		this.abortController = new AbortController();
		const { signal } = this.abortController;
		this.header = this.closest(".header") || document.querySelector(".header");
		this.announcement = document.querySelector(".announcement-bar");
		this.mobileNumber = document.querySelector(".module-number");
		window.addEventListener("scroll", this.handleScroll, { signal });
		this.handleScroll();
		this.initSideMenus(signal);
		if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => this.initMegaMenus(signal), {
			once: true,
			signal
		});
		else this.initMegaMenus(signal);
	}
	/**
	* Removes listeners, timers, and visual state when the element is detached.
	*/
	disconnectedCallback() {
		this.abortController?.abort();
		this.abortController = null;
		if (this.hideTimeout) clearTimeout(this.hideTimeout);
		this.hideTimeout = null;
		this.activeMenu?.classList.remove("active");
		this.activeMenu = null;
		this.isTriggerHovered = false;
		this.isMenuHovered = false;
	}
	/**
	* Updates compact header styles based on the current scroll position.
	*
	* @returns {void}
	*/
	handleScroll() {
		const isScrolled = window.scrollY > 50;
		this.header?.classList.toggle("header--scrolled", isScrolled);
		this.announcement?.classList.toggle("announcement-bar--scrolled", isScrolled);
		this.mobileNumber?.classList.toggle("module-number--scrolled", isScrolled);
	}
	/**
	* Connects the legacy arrow navigation to its corresponding side menus.
	*
	* @param {AbortSignal} signal - Signal used to remove listeners on disconnect.
	* @returns {void}
	*/
	initSideMenus(signal) {
		const arrowMenuItems = this.querySelectorAll(".arrow");
		const allSideMenus = document.querySelectorAll(".side-menu--desktop");
		arrowMenuItems.forEach((item) => {
			item.parentElement?.addEventListener("mouseenter", () => {
				const menuItem = this.querySelector(`.side-menu--desktop[data-linked="${item.dataset.sideMenu}"]`);
				allSideMenus.forEach((sideMenu) => sideMenu.classList.remove("side-menu--active"));
				menuItem?.classList.add("side-menu--active");
			}, { signal });
		});
	}
	/**
	* Connects desktop navigation triggers to their foldout menus.
	*
	* @param {AbortSignal} signal - Signal used to remove listeners on disconnect.
	* @returns {void}
	*/
	initMegaMenus(signal) {
		const menuItems = [...this.querySelectorAll(".mega-menu__top .link")];
		const megaMenus = [...document.querySelectorAll(".mega-menus__menu")];
		menuItems.forEach((menuItem, index) => {
			const dataTarget = menuItem.getAttribute("data-target");
			const indexedMenu = megaMenus[index];
			const relatedMenu = indexedMenu?.getAttribute("data-parent") === dataTarget && indexedMenu.classList.contains("fold-out") ? indexedMenu : megaMenus.find((menu) => menu.getAttribute("data-parent") === dataTarget && menu.classList.contains("fold-out"));
			menuItem.addEventListener("mouseenter", () => {
				this.isTriggerHovered = true;
				if (relatedMenu) this.showMenu(relatedMenu);
				else this.closeActiveMenu();
			}, { signal });
			menuItem.addEventListener("mouseleave", () => {
				this.isTriggerHovered = false;
				this.scheduleMenuHide();
			}, { signal });
		});
		megaMenus.forEach((menu) => {
			menu.addEventListener("mouseenter", () => {
				this.isMenuHovered = true;
				if (this.hideTimeout) clearTimeout(this.hideTimeout);
			}, { signal });
			menu.addEventListener("mouseleave", () => {
				this.isMenuHovered = false;
				this.scheduleMenuHide();
			}, { signal });
		});
	}
	/**
	* Displays a foldout and closes the previously active one.
	*
	* @param {HTMLElement} menu - Foldout menu to display.
	* @returns {void}
	*/
	showMenu(menu) {
		if (this.hideTimeout) clearTimeout(this.hideTimeout);
		if (this.activeMenu !== menu) this.activeMenu?.classList.remove("active");
		menu.classList.add("active");
		this.activeMenu = menu;
	}
	/**
	* Delays closing so the pointer can cross from a trigger into its foldout.
	*
	* @returns {void}
	*/
	scheduleMenuHide() {
		if (this.hideTimeout) clearTimeout(this.hideTimeout);
		this.hideTimeout = setTimeout(() => {
			if (!this.isTriggerHovered && !this.isMenuHovered) this.closeActiveMenu();
		}, 200);
	}
	/**
	* Closes the currently active desktop foldout.
	*
	* @returns {void}
	*/
	closeActiveMenu() {
		this.activeMenu?.classList.remove("active");
		this.activeMenu = null;
	}
};
if (customElements.get("header-handler") === void 0) customElements.define("header-handler", Header);
//#endregion
